import { Rng } from '../rng';
import type { LatLon } from '../geo';

/**
 * Seegang und Stroemung.
 *
 * Bewusst als reine Funktion von Ort und Zeit statt als fortgeschriebener
 * Zustand: Damit laesst sich das Wetter an jedem Punkt der geplanten Route
 * vorausberechnen, ohne die Simulation laufen zu lassen. Der Spieler kann also
 * eine Route bewerten, bevor er ablegt — und genau das ist die Entscheidung,
 * um die es im Reise-Minispiel geht.
 */

/** Seegang nach Douglas-Skala, 0 (spiegelglatt) bis 9 (orkanartig). */
export type SeaState = number;

/**
 * Glatt interpolierendes Wertrauschen.
 * Kein Perlin — fuer Wetterfelder genuegt bilineare Interpolation zwischen
 * zufaelligen Gitterwerten, und sie ist leichter nachvollziehbar.
 */
function valueNoise(seed: string, x: number, y: number, t: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const t0 = Math.floor(t);
  const fx = x - x0;
  const fy = y - y0;
  const ft = t - t0;

  // Weiche Interpolation (smoothstep), damit keine Knicke entstehen.
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const st = ft * ft * (3 - 2 * ft);

  const at = (ix: number, iy: number, it: number): number =>
    Rng.fromSeed(`${seed}:${ix}:${iy}:${it}`).next();

  const lerp = (a: number, b: number, f: number): number => a + (b - a) * f;

  const slice = (it: number): number =>
    lerp(
      lerp(at(x0, y0, it), at(x0 + 1, y0, it), sx),
      lerp(at(x0, y0 + 1, it), at(x0 + 1, y0 + 1, it), sx),
      sy,
    );

  return lerp(slice(t0), slice(t0 + 1), st);
}

export interface WeatherField {
  /** Seegang an Ort und Zeit. */
  seaStateAt(position: LatLon, hours: number): SeaState;
  /** Stroemung an Ort und Zeit. */
  currentAt(position: LatLon, hours: number): { setDeg: number; driftKnots: number };
}

/** Raeumliche Skala des Wetterfelds: ein Rauschgitter je 3 Grad. */
const SPATIAL_SCALE = 3;
/** Zeitliche Skala: ein Rauschgitter je 18 Stunden. */
const TEMPORAL_SCALE = 18;

/**
 * Der Golfstrom laeuft entlang der Ostkueste Floridas nach Nordosten und ist
 * mit bis zu vier Knoten schnell genug, um Fahrzeiten spuerbar zu veraendern.
 * Vereinfacht als Band entlang einer Linie durch die Florida-Strasse.
 */
function gulfStreamInfluence(position: LatLon): { setDeg: number; driftKnots: number } {
  if (position.lat < 24 || position.lat > 34) {
    return { setDeg: 0, driftKnots: 0 };
  }

  // Der Strom folgt bis etwa 28 Grad Nord dicht der Schelfkante vor Florida
  // und schert erst danach nach Nordosten aus. Eine durchgehende Gerade von
  // Sued nach Nordost laege bereits auf Hoehe der Bahamas deutlich zu weit
  // oestlich.
  const eastward = Math.max(0, position.lat - 28) / 6;
  const centreLon = -79.7 + eastward * 5;
  const offsetDeg = Math.abs(position.lon - centreLon);

  // Innerhalb eines Grades voller Einfluss, danach schnell abfallend.
  const strength = Math.max(0, 1 - offsetDeg / 1.6);
  // Weiter noerdlich faechert der Strom auf und dreht nach Osten.
  const setDeg = 20 + eastward * 45;

  return { setDeg, driftKnots: 3.4 * strength };
}

export function createWeather(seed: string): WeatherField {
  const seaSeed = `${seed}::sea`;
  const currentSeed = `${seed}::current`;

  return {
    seaStateAt(position, hours) {
      const raw = valueNoise(
        seaSeed,
        position.lon / SPATIAL_SCALE,
        position.lat / SPATIAL_SCALE,
        hours / TEMPORAL_SCALE,
      );

      // Verteilung so verschieben, dass ruhige See der Normalfall ist und
      // schwerer Sturm die Ausnahme bleibt.
      const skewed = raw ** 1.7;
      return Math.min(9, Math.round(skewed * 11 * 10) / 10);
    },

    currentAt(position, hours) {
      const stream = gulfStreamInfluence(position);

      const wobble = valueNoise(
        currentSeed,
        position.lon / SPATIAL_SCALE,
        position.lat / SPATIAL_SCALE,
        hours / TEMPORAL_SCALE,
      );

      // Ausserhalb des Golfstroms treibt eine schwache, wechselnde Stroemung.
      const ambientDrift = 0.2 + wobble * 0.7;
      const ambientSet = wobble * 360;

      if (stream.driftKnots < ambientDrift) {
        return { setDeg: ambientSet, driftKnots: ambientDrift };
      }
      return { setDeg: stream.setDeg + (wobble - 0.5) * 30, driftKnots: stream.driftKnots };
    },
  };
}

/** Beschreibt den Seegang in Worten — Schluessel fuer die Uebersetzung. */
export function seaStateKey(seaState: SeaState): string {
  if (seaState < 1.5) return 'weather.calm';
  if (seaState < 3.5) return 'weather.moderate';
  if (seaState < 5.5) return 'weather.rough';
  if (seaState < 7.5) return 'weather.heavy';
  return 'weather.storm';
}

/**
 * Wie stark die Stroemung ueber Grund hilft oder bremst.
 * Liefert den Anteil der Stroemung, der auf den Kurs wirkt (-1 bis +1).
 */
export function currentAlongCourse(courseDeg: number, setDeg: number): number {
  const delta = ((setDeg - courseDeg + 540) % 360) - 180;
  return Math.cos((delta * Math.PI) / 180);
}
